
import { useState, useEffect } from 'react';
import {
    Card,
    Grid, Tooltip,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Paper,
    TablePagination,
    Typography,
    Box,
    CircularProgress,
    useTheme,
    useMediaQuery,
    InputLabel,
    FormControl,
    Select,
    MenuItem
} from '@mui/material';

import dayjs from 'dayjs';

import CardHeadMenu from '../Components/Card/CardHeadMenu/CardHeadMenu';
import CustomDateRangePicker from "../Components/DatePicker/CustomDateRangePicker";

import pdfImage from "../Components/Assets/images/pdf.jfif";
import excelImage from "../Components/Assets/images/excel.png";

import configServ from '../services/config';
import { useSelector } from 'react-redux';

import DownloadAttendanceReportPdf from '../Components/Download/ DownloadAttendanceReportPdf';
import DownloadAttendanceReportExcel from '../Components/Download/DownloadAttendanceReportExcel';
import DownloadEmployeeAttendanceReportPdf from '../Components/Download/DownloadEmployeeAttendanceReportPdf';


const AttendanceReport = () => {

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));


    const [departments, setDepartments] = useState([]);
    const [siteList, setSiteList] = useState([]);
    const [departmentFilter, setDepartmentFilter] = useState("");
    const [siteFilter, setSiteFilter] = useState("");

    // ============================================================
    // STATE MANAGEMENT
    // ============================================================

    const [attendanceData, setAttendanceData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchCustomer, setSearchCustomer] = useState('');

    const { company_id, companyName } = useSelector(
        (state) => state.GlobalVariables
    );

    // Default Date:
    // Current month first day -> Today
    const [dateRange, setDateRange] = useState([
        dayjs().startOf('month'),
        dayjs()
    ]);

    // Pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Company Logo
    const [companyLogo, setCompanyLogo] = useState("");

    const [openBackdrop, setOpenBackdrop] = useState(false);

    // Sorting
    const [orderBy, setOrderBy] = useState('employee_name');
    const [order, setOrder] = useState('asc');


    // ============================================================
    // COMPANY LOGO
    // ============================================================

    const assignCompanyLogo = async () => {
        try {

            const result = await configServ.company_logo({
                company_id: company_id
            });

            if (result && result.image) {

                setCompanyLogo(
                    `data:image/png;base64,${result.image}`
                );

            } else {

                setCompanyLogo(null);

            }

        } catch (e) {

            console.log(e);
            setCompanyLogo(null);

        }
    };


    useEffect(() => {

        assignCompanyLogo();

    }, [company_id]);


    // ============================================================
    // FETCH ATTENDANCE REPORT
    // ============================================================

    const fetchAttendanceReports = async () => {
        setLoading(true);

        try {
            const response = await configServ.getAttendanceReports({
                company_id: company_id,

                start_date: dateRange[0]
                    ? dayjs(dateRange[0]).format("YYYY-MM-DD")
                    : "",

                end_date: dateRange[1]
                    ? dayjs(dateRange[1]).format("YYYY-MM-DD")
                    : "",

                search: searchCustomer,

                department_id: departmentFilter || undefined,

                site_id: siteFilter || undefined,

                sort_by: orderBy,

                sort_order: order
            });

            if (response && response.status) {
                setAttendanceData(
                    response.data.data || response.data
                );
            }
        } catch (error) {
            console.error("Error:", error);
            setAttendanceData([]);
        } finally {
            setTimeout(() => setLoading(false), 500);
        }
    };

    useEffect(() => {
        fetchAttendanceReports();
        setPage(0);
    }, [
        dateRange,
        searchCustomer,
        departmentFilter,
        siteFilter,
        order,
        orderBy
    ]);


    // ============================================================
    // PAGINATION
    // ============================================================

    const handleChangePage = (event, newPage) => {

        setPage(newPage);

    };


    const handleChangeRowsPerPage = (event) => {

        setRowsPerPage(
            parseInt(event.target.value, 10)
        );

        setPage(0);

    };


    // ============================================================
    // SORTING
    // ============================================================

    const handleSort = (property) => {

        const isAsc =
            orderBy === property &&
            order === 'asc';

        setOrder(
            isAsc ? 'desc' : 'asc'
        );

        setOrderBy(property);

    };


    // ============================================================
    // DOWNLOAD
    // ============================================================

    const handleDownloadPdf = () => {

        DownloadAttendanceReportPdf(
            attendanceData,
            companyName,
            companyLogo,
            setOpenBackdrop,
            dateRange
        );

    };


    const handleDownloadExcel = () => {

        DownloadAttendanceReportExcel(
            attendanceData,
            companyName,
            companyLogo,
            setOpenBackdrop,
            dateRange
        );

    };


    // ============================================================
    // FRONTEND PAGINATION
    // ============================================================

    const paginatedData =
        attendanceData.slice(
            page * rowsPerPage,
            page * rowsPerPage + rowsPerPage
        );


    // ============================================================
    // SINGLE EMPLOYEE PDF
    // ============================================================

    const handleDownloadSingleEmployeePdf = async (employeeData) => {

        try {

            setOpenBackdrop(true);

            const payload = {

                company_id: company_id,

                employee_id: employeeData.employee_id,

                start_date: dateRange[0]
                    ? dayjs(dateRange[0]).format("YYYY-MM-DD")
                    : "",

                end_date: dateRange[1]
                    ? dayjs(dateRange[1]).format("YYYY-MM-DD")
                    : ""

            };


            const response =
                await configServ.employeeAttendanceReport(
                    payload
                );


            if (response.status === 200) {

                DownloadEmployeeAttendanceReportPdf(
                    response,
                    companyName,
                    companyLogo,
                    setOpenBackdrop,
                    dateRange
                );

            }

        } catch (error) {

            console.error("Error:", error);

            alert(
                "Failed to download employee attendance report"
            );

        } finally {

            setOpenBackdrop(false);

        }

    };


    // Fetch Departments
    const fetchDepartments = async () => {
        try {
            if (company_id) {
                const deptRes = await configServ.getAllDepartments(company_id);
                if (deptRes?.data) {
                    setDepartments(deptRes.data);
                } else if (Array.isArray(deptRes)) {
                    setDepartments(deptRes);
                }
            }
        } catch (e) {
            console.error("Error fetching departments:", e);
        }
    };

    // Fetch Sites (Filtered by type "Standard")
    const fetchSiteList = async () => {
        try {
            const result = await configServ.get_customer({ company_id });
            const filteredSites = result?.filter((item) => item.type === "Standard");
            setSiteList(filteredSites || []);
        } catch (e) {
            console.error("Error fetching site list:", e);
        }
    };

    useEffect(() => {
        fetchDepartments();
        fetchSiteList();
    }, []);


    // ============================================================
    // UI
    // ============================================================

    return (

        <Box
            sx={{
                p: 2,
                width: '100%'
            }}
        >

            {/* ====================================================
                PAGE HEADER
            ==================================================== */}

            <CardHeadMenu
                prefix={"Na"}
                suffix={"Na"}
                title={"ATTENDANCE Reports"}
                showCreate={false}
            />


            {/* ====================================================
                FILTER CARD
            ==================================================== */}

            <Card
                sx={{
                    p: 2,
                    mb: 3,
                    borderRadius: "12px",
                    borderLeft: "6px solid #1976d2",
                    width: "100%",
                    boxShadow: 2,
                }}
            >
                <Grid container spacing={2} alignItems="center">
                    {/* ====================================================
        DATE RANGE
    ==================================================== */}
                    <Grid item xs={12} sm={6} md={3}>
                        <CustomDateRangePicker
                            value={dateRange}
                            onChange={setDateRange}
                            fullWidth
                        />
                    </Grid>

                    {/* ====================================================
        DEPARTMENT DROPDOWN
    ==================================================== */}
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="department-select-label">Department</InputLabel>
                            <Select
                                labelId="department-select-label"
                                id="department_id"
                                name="department_id"
                                value={departmentFilter}
                                label="Department"
                                onChange={(e) => setDepartmentFilter(e.target.value)}
                            >
                                <MenuItem value="">
                                    <em>All Departments</em>
                                </MenuItem>
                                {departments?.map((dept) => (
                                    <MenuItem key={dept.id} value={dept.id}>
                                        {dept.name || dept.department_name || `Dept #${dept.id}`}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* ====================================================
        SITE / LOCATION DROPDOWN
    ==================================================== */}
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="site-select-label">Site / Location</InputLabel>
                            <Select
                                labelId="site-select-label"
                                id="site_id"
                                name="site_id"
                                value={siteFilter}
                                label="Site / Location"
                                onChange={(e) => setSiteFilter(e.target.value)}
                            >
                                <MenuItem value="">
                                    <em>All Sites</em>
                                </MenuItem>
                                {siteList?.map((site) => (
                                    <MenuItem key={site.id} value={site.id}>
                                        {site.contact_name || `Site #${site.id}`}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* ====================================================
        EXPORT BUTTONS
    ==================================================== */}
                    <Grid
                        item
                        xs={12}
                        sm={6}
                        md={3}
                        sx={{
                            display: "flex",
                            justifyContent: {
                                xs: "flex-start",
                                md: "flex-end",
                            },
                            gap: 1,
                        }}
                    >
                        <Tooltip title="Download PDF">
                            <IconButton onClick={handleDownloadPdf}>
                                <img
                                    src={pdfImage}
                                    alt="pdf"
                                    style={{
                                        width: 28,
                                        height: 28,
                                    }}
                                />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title="Download Excel">
                            <IconButton onClick={handleDownloadExcel}>
                                <img
                                    src={excelImage}
                                    alt="excel"
                                    style={{
                                        width: 28,
                                        height: 28,
                                    }}
                                />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                </Grid>
            </Card>


            {/* ====================================================
                LOADING
            ==================================================== */}

            {loading ? (

                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        my: 5
                    }}
                >

                    <CircularProgress />

                </Box>


            ) : attendanceData.length === 0 ? (

                <Typography
                    variant="body1"
                    textAlign="center"
                    sx={{ my: 4 }}
                >
                    No records found.
                </Typography>


            ) : isMobile ? (

                /* ====================================================
                   MOBILE VIEW
                ==================================================== */

                <Box>

                    <Grid
                        container
                        spacing={2}
                    >

                        {paginatedData.map(
                            (emp) => (

                                <Grid
                                    item
                                    xs={12}
                                    key={emp.employee_id}
                                >

                                    <Card
                                        sx={{
                                            p: 2,
                                            borderRadius: '8px',
                                            boxShadow: 1,
                                            borderLeft:
                                                '4px solid #1976d2'
                                        }}
                                    >

                                        {/* Employee Name */}

                                        <Typography
                                            variant="subtitle1"
                                            fontWeight="bold"
                                            color="primary.main"
                                        >
                                            {emp.employee_name}
                                        </Typography>


                                        {/* Employee Details */}

                                        <Typography
                                            variant="body2"
                                            color="textSecondary"
                                            sx={{ mb: 1 }}
                                        >
                                            {emp.email || 'No Email'}
                                            {" | "}
                                            {emp.address}
                                        </Typography>


                                        {/* Attendance Summary */}

                                        <Grid
                                            container
                                            spacing={1}
                                            sx={{
                                                mt: 1,
                                                pt: 1,
                                                borderTop:
                                                    '1px solid #eee'
                                            }}
                                        >

                                            <Grid item xs={4}>

                                                <Typography variant="caption">

                                                    Total:
                                                    {" "}
                                                    <b>
                                                        {emp.total_days}
                                                    </b>

                                                </Typography>

                                            </Grid>


                                            <Grid item xs={4}>

                                                <Typography variant="caption">

                                                    Department:
                                                    {" "}
                                                    <b>
                                                        {emp.department}
                                                    </b>

                                                </Typography>

                                            </Grid>


                                            <Grid item xs={4}>

                                                <Typography variant="caption">

                                                    Designation:
                                                    {" "}
                                                    <b>
                                                        {emp.designation}
                                                    </b>

                                                </Typography>

                                            </Grid>


                                            <Grid item xs={4}>

                                                <Typography
                                                    variant="caption"
                                                    color="success.main"
                                                >

                                                    Present:
                                                    {" "}
                                                    <b>
                                                        {emp.present}
                                                    </b>

                                                </Typography>

                                            </Grid>


                                            <Grid item xs={4}>

                                                <Typography
                                                    variant="caption"
                                                    color="error.main"
                                                >

                                                    Absent:
                                                    {" "}
                                                    <b>
                                                        {emp.absent}
                                                    </b>

                                                </Typography>

                                            </Grid>


                                            <Grid item xs={4}>

                                                <Typography
                                                    variant="caption"
                                                    color="warning.main"
                                                >

                                                    Leave:
                                                    {" "}
                                                    <b>
                                                        {emp.leave}
                                                    </b>

                                                </Typography>

                                            </Grid>


                                            {/* ====================================================
                                                HOLIDAY
                                            ==================================================== */}

                                            <Grid item xs={4}>

                                                <Typography
                                                    variant="caption"
                                                    color="info.main"
                                                >

                                                    Holiday:
                                                    {" "}
                                                    <b>
                                                        {emp.holiday}
                                                    </b>

                                                </Typography>

                                            </Grid>


                                            {/* ====================================================
                                                NEW WEEK OFF
                                            ==================================================== */}

                                            <Grid item xs={4}>

                                                <Typography
                                                    variant="caption"
                                                    color="secondary.main"
                                                >

                                                    Week Off:
                                                    {" "}
                                                    <b>
                                                        {emp.week_off}
                                                    </b>

                                                </Typography>

                                            </Grid>

                                        </Grid>

                                    </Card>

                                </Grid>

                            )
                        )}

                    </Grid>


                    {/* Mobile Pagination */}

                    <TablePagination
                        component="div"
                        count={attendanceData.length}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={
                            handleChangeRowsPerPage
                        }
                        rowsPerPageOptions={[
                            5,
                            10,
                            25
                        ]}
                        sx={{ mt: 2 }}
                    />

                </Box>


            ) : (

                /* ====================================================
                   DESKTOP VIEW
                ==================================================== */

                <Paper
                    sx={{
                        width: '100%',
                        overflow: 'hidden',
                        borderRadius: '12px',
                        boxShadow: 2
                    }}
                >

                    <TableContainer>

                        <Table size="small">

                            {/* ====================================================
                               TABLE HEADER
                            ==================================================== */}

                            <TableHead
                                sx={{
                                    backgroundColor: '#f5f5f5'
                                }}
                            >

                                <TableRow>


                                    {/* S.NO */}

                                    <TableCell
                                        sx={{
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        S.No.
                                    </TableCell>


                                    {/* EMPLOYEE NAME */}

                                    <TableCell
                                        sx={{
                                            fontWeight: 'bold'
                                        }}
                                        sortDirection={
                                            orderBy === 'employee_name'
                                                ? order
                                                : false
                                        }
                                    >

                                        <TableSortLabel
                                            active={
                                                orderBy ===
                                                'employee_name'
                                            }
                                            direction={
                                                orderBy ===
                                                    'employee_name'
                                                    ? order
                                                    : 'asc'
                                            }
                                            onClick={() =>
                                                handleSort(
                                                    'employee_name'
                                                )
                                            }
                                        >

                                            Employee Name

                                        </TableSortLabel>

                                    </TableCell>


                                    {/* EMAIL */}

                                    <TableCell
                                        sx={{
                                            fontWeight: 'bold'
                                        }}
                                        sortDirection={
                                            orderBy === 'email'
                                                ? order
                                                : false
                                        }
                                    >

                                        <TableSortLabel
                                            active={
                                                orderBy === 'email'
                                            }
                                            direction={
                                                orderBy === 'email'
                                                    ? order
                                                    : 'asc'
                                            }
                                            onClick={() =>
                                                handleSort('email')
                                            }
                                        >

                                            Email

                                        </TableSortLabel>

                                    </TableCell>


                                    {/* ADDRESS */}

                                    <TableCell
                                        sx={{
                                            fontWeight: 'bold'
                                        }}
                                        sortDirection={
                                            orderBy === 'address'
                                                ? order
                                                : false
                                        }
                                    >

                                        <TableSortLabel
                                            active={
                                                orderBy === 'address'
                                            }
                                            direction={
                                                orderBy === 'address'
                                                    ? order
                                                    : 'asc'
                                            }
                                            onClick={() =>
                                                handleSort('address')
                                            }
                                        >

                                            Address

                                        </TableSortLabel>

                                    </TableCell>


                                    {/* DEPARTMENT */}

                                    <TableCell
                                        sx={{
                                            fontWeight: 'bold'
                                        }}
                                        align="center"
                                        sortDirection={
                                            orderBy === 'department'
                                                ? order
                                                : false
                                        }
                                    >

                                        <TableSortLabel
                                            active={
                                                orderBy === 'department'
                                            }
                                            direction={
                                                orderBy === 'department'
                                                    ? order
                                                    : 'asc'
                                            }
                                            onClick={() =>
                                                handleSort(
                                                    'department'
                                                )
                                            }
                                        >

                                            Designation

                                        </TableSortLabel>

                                    </TableCell>


                                    {/* DESIGNATION */}

                                    <TableCell
                                        sx={{
                                            fontWeight: 'bold'
                                        }}
                                        align="center"
                                        sortDirection={
                                            orderBy === 'designation'
                                                ? order
                                                : false
                                        }
                                    >

                                        <TableSortLabel
                                            active={
                                                orderBy === 'designation'
                                            }
                                            direction={
                                                orderBy === 'designation'
                                                    ? order
                                                    : 'asc'
                                            }
                                            onClick={() =>
                                                handleSort(
                                                    'designation'
                                                )
                                            }
                                        >

                                            Department

                                        </TableSortLabel>

                                    </TableCell>


                                    {/* TOTAL DAYS */}

                                    <TableCell
                                        sx={{
                                            fontWeight: 'bold'
                                        }}
                                        align="center"
                                        sortDirection={
                                            orderBy === 'total_days'
                                                ? order
                                                : false
                                        }
                                    >

                                        <TableSortLabel
                                            active={
                                                orderBy === 'total_days'
                                            }
                                            direction={
                                                orderBy === 'total_days'
                                                    ? order
                                                    : 'asc'
                                            }
                                            onClick={() =>
                                                handleSort(
                                                    'total_days'
                                                )
                                            }
                                        >

                                            Total Days

                                        </TableSortLabel>

                                    </TableCell>


                                    {/* PRESENT */}

                                    <TableCell
                                        sx={{
                                            fontWeight: 'bold',
                                            color: 'success.main'
                                        }}
                                        align="center"
                                        sortDirection={
                                            orderBy === 'present'
                                                ? order
                                                : false
                                        }
                                    >

                                        <TableSortLabel
                                            active={
                                                orderBy === 'present'
                                            }
                                            direction={
                                                orderBy === 'present'
                                                    ? order
                                                    : 'asc'
                                            }
                                            onClick={() =>
                                                handleSort('present')
                                            }
                                            sx={{
                                                color:
                                                    'success.main !important',

                                                '& .MuiTableSortLabel-icon':
                                                {
                                                    color:
                                                        'success.main !important'
                                                }
                                            }}
                                        >

                                            Present

                                        </TableSortLabel>

                                    </TableCell>


                                    {/* ABSENT */}

                                    <TableCell
                                        sx={{
                                            fontWeight: 'bold',
                                            color: 'error.main'
                                        }}
                                        align="center"
                                        sortDirection={
                                            orderBy === 'absent'
                                                ? order
                                                : false
                                        }
                                    >

                                        <TableSortLabel
                                            active={
                                                orderBy === 'absent'
                                            }
                                            direction={
                                                orderBy === 'absent'
                                                    ? order
                                                    : 'asc'
                                            }
                                            onClick={() =>
                                                handleSort('absent')
                                            }
                                            sx={{
                                                color:
                                                    'error.main !important',

                                                '& .MuiTableSortLabel-icon':
                                                {
                                                    color:
                                                        'error.main !important'
                                                }
                                            }}
                                        >

                                            Absent

                                        </TableSortLabel>

                                    </TableCell>


                                    {/* LEAVE */}

                                    <TableCell
                                        sx={{
                                            fontWeight: 'bold',
                                            color: 'warning.main'
                                        }}
                                        align="center"
                                        sortDirection={
                                            orderBy === 'leave'
                                                ? order
                                                : false
                                        }
                                    >

                                        <TableSortLabel
                                            active={
                                                orderBy === 'leave'
                                            }
                                            direction={
                                                orderBy === 'leave'
                                                    ? order
                                                    : 'asc'
                                            }
                                            onClick={() =>
                                                handleSort('leave')
                                            }
                                            sx={{
                                                color:
                                                    'warning.main !important',

                                                '& .MuiTableSortLabel-icon':
                                                {
                                                    color:
                                                        'warning.main !important'
                                                }
                                            }}
                                        >

                                            Leave

                                        </TableSortLabel>

                                    </TableCell>


                                    {/* HOLIDAY */}

                                    <TableCell
                                        sx={{
                                            fontWeight: 'bold',
                                            color: 'info.main'
                                        }}
                                        align="center"
                                        sortDirection={
                                            orderBy === 'holiday'
                                                ? order
                                                : false
                                        }
                                    >

                                        <TableSortLabel
                                            active={
                                                orderBy === 'holiday'
                                            }
                                            direction={
                                                orderBy === 'holiday'
                                                    ? order
                                                    : 'asc'
                                            }
                                            onClick={() =>
                                                handleSort('holiday')
                                            }
                                            sx={{
                                                color:
                                                    'info.main !important',

                                                '& .MuiTableSortLabel-icon':
                                                {
                                                    color:
                                                        'info.main !important'
                                                }
                                            }}
                                        >

                                            Holiday

                                        </TableSortLabel>

                                    </TableCell>


                                    {/* ====================================================
                                       NEW WEEK OFF COLUMN
                                    ==================================================== */}

                                    <TableCell
                                        sx={{
                                            fontWeight: 'bold',
                                            color: 'secondary.main'
                                        }}
                                        align="center"
                                        sortDirection={
                                            orderBy === 'week_off'
                                                ? order
                                                : false
                                        }
                                    >

                                        <TableSortLabel
                                            active={
                                                orderBy === 'week_off'
                                            }
                                            direction={
                                                orderBy === 'week_off'
                                                    ? order
                                                    : 'asc'
                                            }
                                            onClick={() =>
                                                handleSort('week_off')
                                            }
                                            sx={{
                                                color:
                                                    'secondary.main !important',

                                                '& .MuiTableSortLabel-icon':
                                                {
                                                    color:
                                                        'secondary.main !important'
                                                }
                                            }}
                                        >

                                            Week Off

                                        </TableSortLabel>

                                    </TableCell>


                                    {/* ACTION */}

                                    <TableCell
                                        sx={{
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        Action
                                    </TableCell>


                                </TableRow>

                            </TableHead>


                            {/* ====================================================
                               TABLE BODY
                            ==================================================== */}

                            <TableBody>

                                {paginatedData.map(
                                    (emp, index) => (

                                        <TableRow
                                            key={emp.employee_id}
                                            hover
                                        >

                                            {/* S.NO */}

                                            <TableCell>
                                                {
                                                    page *
                                                    rowsPerPage +
                                                    index +
                                                    1
                                                }
                                            </TableCell>


                                            {/* EMPLOYEE */}

                                            <TableCell
                                                sx={{
                                                    fontWeight: 500
                                                }}
                                            >
                                                {emp.employee_name}
                                            </TableCell>


                                            {/* EMAIL */}

                                            <TableCell>
                                                {emp.email || '—'}
                                            </TableCell>


                                            {/* ADDRESS */}

                                            <TableCell
                                                sx={{
                                                    maxWidth: '200px',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis'
                                                }}
                                            >
                                                {emp.address}
                                            </TableCell>


                                            {/* DESIGNATION */}

                                            <TableCell align="center">
                                                {emp.designation}
                                            </TableCell>


                                            {/* DEPARTMENT */}

                                            <TableCell align="center">
                                                {emp.department}
                                            </TableCell>


                                            {/* TOTAL */}

                                            <TableCell align="center">
                                                {emp.total_days}
                                            </TableCell>


                                            {/* PRESENT */}

                                            <TableCell
                                                align="center"
                                                sx={{
                                                    color: 'success.main',
                                                    fontWeight: 'bold'
                                                }}
                                            >
                                                {emp.present}
                                            </TableCell>


                                            {/* ABSENT */}

                                            <TableCell
                                                align="center"
                                                sx={{
                                                    color: 'error.main',
                                                    fontWeight: 'bold'
                                                }}
                                            >
                                                {emp.absent}
                                            </TableCell>


                                            {/* LEAVE */}

                                            <TableCell
                                                align="center"
                                            >
                                                {emp.leave}
                                            </TableCell>


                                            {/* HOLIDAY */}

                                            <TableCell
                                                align="center"
                                            >
                                                {emp.holiday}
                                            </TableCell>


                                            {/* ====================================================
                                               NEW WEEK OFF VALUE
                                            ==================================================== */}

                                            <TableCell
                                                align="center"
                                                sx={{
                                                    color:
                                                        'secondary.main',
                                                    fontWeight: 'bold'
                                                }}
                                            >
                                                {emp.week_off}
                                            </TableCell>


                                            {/* ACTION */}

                                            <TableCell>

                                                <Tooltip
                                                    title="Download PDF"
                                                >

                                                    <IconButton
                                                        onClick={() =>
                                                            handleDownloadSingleEmployeePdf(
                                                                emp
                                                            )
                                                        }
                                                    >

                                                        <img
                                                            src={pdfImage}
                                                            alt="pdf"
                                                            style={{
                                                                width: 28,
                                                                height: 28
                                                            }}
                                                        />

                                                    </IconButton>

                                                </Tooltip>

                                            </TableCell>


                                        </TableRow>

                                    )
                                )}

                            </TableBody>

                        </Table>

                    </TableContainer>


                    {/* ====================================================
                       DESKTOP PAGINATION
                    ==================================================== */}

                    <TablePagination
                        rowsPerPageOptions={[
                            5,
                            10,
                            25
                        ]}
                        component="div"
                        count={attendanceData.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={
                            handleChangeRowsPerPage
                        }
                    />

                </Paper>

            )}

        </Box>

    );

};


export default AttendanceReport;

